import { Button, Checkbox, Icon, theme, Tooltip, Text } from 'components';
import * as React from 'react';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import { featureEnabled } from 'utils';

export type NotificationOptions = {
  feedbackReminders: boolean;
  propertyAnnouncements: boolean;
  registeredOffers: boolean;
  priceChanges: boolean;
  statusChanges: boolean;
};

type AppointmentPropertyUpdateProps = {
  cancelRoute?: string;
  isLoading: boolean;
  listingAddress: string;
  newNotifications: NotificationOptions;
  onNotificationOptionsChange: (
    notifications: Partial<NotificationOptions>,
  ) => void;
  saveNotifications: () => void;
};

const AppointmentPropertyUpdate: React.FunctionComponent<
  AppointmentPropertyUpdateProps
> = ({
  cancelRoute,
  isLoading,
  newNotifications,
  onNotificationOptionsChange,
  saveNotifications,
}) => {
  const handleOptionChange = (e: any, field: keyof NotificationOptions) => {
    const updatedValue = e.target.checked;

    onNotificationOptionsChange({ [field]: updatedValue });
  };
  const { md } = useBreakpoint();
  if (!featureEnabled('propertyNotificationsRevampFeature', 1)) {
    return (
      <div style={{ width: '100%' }}>
        <RowContainer>
          <ColContainer>
            <NotifyHeading>Notify me regarding:</NotifyHeading>
            <Border />
            <CheckBoxContainer $md={md}>
              <StyledCheckboxOld
                checked={newNotifications.registeredOffers}
                onChange={e => handleOptionChange(e, 'registeredOffers')}
              >
                Registered Offers:
              </StyledCheckboxOld>

              <StyledCheckboxOld
                checked={newNotifications.propertyAnnouncements}
                onChange={e => handleOptionChange(e, 'propertyAnnouncements')}
              >
                Property Announcements:
              </StyledCheckboxOld>

              <StyledCheckboxOld
                checked={newNotifications.feedbackReminders}
                onChange={e => handleOptionChange(e, 'feedbackReminders')}
              >
                Feedback Reminders:
              </StyledCheckboxOld>

              <br />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                {cancelRoute && (
                  <a
                    className="btn btn-default"
                    style={{ marginRight: '10px' }}
                    href={cancelRoute}
                  >
                    Cancel
                  </a>
                )}
                <StyledSaveButton
                  type="primary"
                  onClick={saveNotifications}
                  disabled={isLoading}
                >
                  Save
                </StyledSaveButton>
              </div>
            </CheckBoxContainer>
          </ColContainer>
        </RowContainer>
      </div>
    );
  }
  return (
    <div style={{ width: '100%' }}>
      <RowContainer>
        <ColContainer>
          <StyledInfoText>
            Please select and save your preferences to opt in for notifications
            for this property. If you would like to opt out of these
            notifications, simply uncheck all the checkboxes and save.
          </StyledInfoText>

          <Border />

          <StyledGrid>
            <StyledCheckbox
              checked={newNotifications.propertyAnnouncements}
              onChange={e => handleOptionChange(e, 'propertyAnnouncements')}
            >
              Property Announcements
              <Tooltip
                placement="right"
                title="Turning on this notification enables you to receive updates about the property communicated by the listing management team."
              >
                <span>
                  <Icon type="antdInfoCircleOutlined" offset={[8, 2]} />
                </span>
              </Tooltip>
            </StyledCheckbox>

            <StyledCheckbox
              checked={newNotifications.registeredOffers}
              onChange={e => handleOptionChange(e, 'registeredOffers')}
            >
              Registered Offers
            </StyledCheckbox>

            <StyledCheckbox
              checked={newNotifications.feedbackReminders}
              onChange={e => handleOptionChange(e, 'feedbackReminders')}
            >
              Feedback Reminders
            </StyledCheckbox>

            {featureEnabled('propertyNotificationsRevampFeature', 2) && (
              <>
                <StyledCheckbox
                  checked={newNotifications.priceChanges}
                  onChange={e => handleOptionChange(e, 'priceChanges')}
                >
                  Price Changes
                </StyledCheckbox>

                <StyledCheckbox
                  checked={newNotifications.statusChanges}
                  onChange={e => handleOptionChange(e, 'statusChanges')}
                >
                  Status Changes
                </StyledCheckbox>
              </>
            )}
          </StyledGrid>
        </ColContainer>
      </RowContainer>
    </div>
  );
};

export default AppointmentPropertyUpdate;

const StyledSaveButton = styled(Button)`
  && {
    color: white;
  }
`;

const ColContainer = styled.div`
  position: relative;
  min-height: 1px;
  padding-right: 15px;
  padding-left: 15px;
`;

const CheckBoxContainer = styled.div<{ $md: boolean }>`
  width: ${({ $md }) => ($md ? '30%' : '100%')};
`;

const RowContainer = styled.div`
  margin: 0px -15px;
`;

const NotifyHeading = styled.h3`
  font-weight: 300;
  text-transform: none;
  font-size: 24px;
  color: ${theme.gray7};
`;

const StyledInfoText = styled(Text)`
  color: ${theme.gray7};
`;

const Border = styled.hr`
  width: 100%;
  margin: 10px 0px;
  border: 1px solid ${theme.gray4};
`;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 50px;
  margin-left: 8px;
`;

const StyledCheckboxOld = styled(Checkbox)`
  && {
    margin-left: 0;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    font-weight: bold;
    margin-top: 10px;
  }
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  grid-template-rows: auto auto;
  gap: 10px;
  padding: 10px;
`;
